import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import { ENABLE_CONSIGNMENT_NOTES } from 'constants/consignment-notes-config'
import {
  isInventoryShoesProduct,
  prepareInventoryShoesUpdatePayload,
  resolveInventoryBulkPhotoFields,
  resolveInventoryBulkPhotoKindOptions,
  validateInventoryShoesRequiredFields,
  getInventoryShoesMissingRequiredWarnings,
  resolveInventoryShoesMissingRequirementTab
} from 'domains/inventory/utils/inventory-shoes-v2-helper'
import TitlePage from 'components/atoms/title-page'
import { InputField } from 'components'
import Select from 'react-select'
import { ALERT_TYPES } from 'constants/alert-constants'
import { normalizeInventoryEditPermissions, normalizeInventoryPricePermissions } from 'domains/inventory/utils/inventory-price-permission-util'
import { useAuth } from 'providers/auth-provider'
import { Form, Formik, useField, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  TabContent,
  TabPane
} from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const optionPropTypes = {
  options: PropTypes.array
}

const fileFieldPropTypes = {
  name     : PropTypes.string.isRequired,
  label    : PropTypes.string.isRequired,
  multiple : PropTypes.bool,
  disabled : PropTypes.bool
}

const checkboxFieldPropTypes = {
  name     : PropTypes.string.isRequired,
  label    : PropTypes.string.isRequired,
  disabled : PropTypes.bool
}

const selectArrayDropdownFieldPropTypes = {
  name     : PropTypes.string.isRequired,
  label    : PropTypes.string.isRequired,
  options  : PropTypes.array,
  disabled : PropTypes.bool
}

const searchableSelectFieldPropTypes = {
  name        : PropTypes.string.isRequired,
  label       : PropTypes.string.isRequired,
  options     : PropTypes.array,
  placeholder : PropTypes.string,
  disabled    : PropTypes.bool
}

const photoPreviewGridPropTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      key           : PropTypes.string,
      label         : PropTypes.string,
      url           : PropTypes.string,
      attachmentId  : PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
      attachmentName: PropTypes.string
    })
  ),
  canDelete            : PropTypes.bool,
  deletingAttachmentId : PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
  onDeletePhoto        : PropTypes.func
}

const inlineTabHeaderPropTypes = {
  activeTab   : PropTypes.string.isRequired,
  onTabChange : PropTypes.func.isRequired
}

const bulkPhotoUploadFieldPropTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  }),
  disabled: PropTypes.bool
}

const TAB = {
  PRODUCT : 'product',
  PHOTOS  : 'photos'
}

const textFieldsLeft = [
  { name: 'supplier_name', label: 'Supplier name' },
  { name: 'stamp', label: 'Stamp' },
  { name: 'leather', label: 'Leather' },
  { name: 'hardware', label: 'Hardware' },
  { name: 'p_colour', label: 'Product Colour' }
]

const measurements = [
  { name: 'p_size', label: 'Product Size' },
  { name: 'height', label: 'Height' },
  { name: 'length', label: 'Length' },
  { name: 'width', label: 'Width' },
  { name: 'handle_drop', label: 'Handle Drop' },
  { name: 'strap_drop', label: 'Strap Drop' }
]

const priceFields = [
  { name: 'buying_price', label: 'Buying Price', viewPermission: 'canViewBuyingPrice', editPermission: 'canEditBuyingPrice' },
  { name: 'default_price', label: 'Default price', viewPermission: 'canViewDefaultPrice', editPermission: 'canEditDefaultPrice' },
  { name: 'selling_price', label: 'Selling price', viewPermission: 'canViewSellingPrice', editPermission: 'canEditSellingPrice' }
]

const photoFields = [
  { name: 'front_view', label: 'Front view' },
  { name: 'right_side_view', label: 'Right side view' },
  { name: 'rear_view', label: 'Rear view' },
  { name: 'left_side_view', label: 'Left side view' },
  { name: 'top_view', label: 'Top view' },
  { name: 'bottom_view', label: 'Bottom view' },
  { name: 'photo_details', label: 'Photo details', multiple: true },
  { name: 'comes_with_image', label: 'Foto kelengkapan' },
  { name: 'stamp_image', label: 'Foto stamp' },
  { name: 'card_image', label: 'Foto Card' }
]

const inventoryProductUploadFileFields = [
  ...photoFields.map((field) => field.name),
  'authentication_certificate'
]
const INVENTORY_PRODUCT_UPLOAD_MAX_FILES = 40
const INVENTORY_PRODUCT_BULK_PHOTO_KIND_OPTIONS = [
  { value: '', label: 'Choose image type' },
  { value: 'front_view', label: 'Front view' },
  { value: 'right_side_view', label: 'Right side view' },
  { value: 'rear_view', label: 'Rear view' },
  { value: 'left_side_view', label: 'Left side view' },
  { value: 'top_view', label: 'Top view' },
  { value: 'bottom_view', label: 'Bottom view' },
  { value: 'comes_with_image', label: 'Foto kelengkapan' },
  { value: 'stamp_image', label: 'Foto stamp' },
  { value: 'card_image', label: 'Foto Card' },
  { value: 'photo_details', label: 'Photo details' }
]
const inventoryProductBulkPhotoAutoKinds = [
  'front_view',
  'right_side_view',
  'rear_view',
  'left_side_view',
  'top_view',
  'bottom_view',
  'comes_with_image',
  'stamp_image',
  'card_image',
  'photo_details'
]

const INVENTORY_PRICE_EDITABLE_FIELDS = new Set([
  'buying_price',
  'default_price',
  'selling_price'
])
const INVENTORY_SPECIFICATION_EDITABLE_FIELDS = new Set([
  'comes_with',
  'product_description',
  'stamp',
  'leather',
  'hardware',
  'p_colour',
  'details',
  'p_size',
  'insole_size',
  'height',
  'length',
  'width',
  'handle_drop',
  'strap_drop',
  'condition_id',
  'defects',
  'size',
  'colour_ids',
  'badge_ids',
  'style_ids'
])
const INVENTORY_LOCATION_EDITABLE_FIELDS = new Set([
  'item_location_id',
  'box_location_id',
  'inclusions_location_id'
])
const INVENTORY_PHOTO_EDITABLE_FIELDS = new Set([
  'insole_photo',
  'photo_folder_link',
  ...photoFields.map((field) => field.name)
])

const canEditInventoryField = (fieldName, permissions = {}) => {
  if ([ 'shipment_cost', 'authentication_cost', 'total_modal_cost' ].includes(fieldName) && !permissions.canViewCosts) return false
  if (fieldName === 'sourced_by') return permissions.canEditSourcingPic
  if (fieldName === 'sku') return permissions.canEditSku
  if (fieldName === 'product_curator_pic') return permissions.canEditProductCuratorPic
  if (INVENTORY_PRICE_EDITABLE_FIELDS.has(fieldName)) {
    return Array.isArray(permissions.editablePriceFields)
      ? permissions.editablePriceFields.includes(fieldName)
      : permissions.canEditPrices
  }
  if (permissions.canEditAllProductFields) return true
  if (Array.isArray(permissions.editableProductFields) && permissions.editableProductFields.includes(fieldName)) return true
  if (permissions.canEditSpecifications && INVENTORY_SPECIFICATION_EDITABLE_FIELDS.has(fieldName)) return true
  if (permissions.canEditLocations && INVENTORY_LOCATION_EDITABLE_FIELDS.has(fieldName)) return true
  if (permissions.canEditPhotos && INVENTORY_PHOTO_EDITABLE_FIELDS.has(fieldName)) return true

  return false
}

const editableInventoryPayload = (values = {}, permissions = {}) => Object.keys(values).reduce((payload, fieldName) => {
  if (fieldName === 'appraisal_state_label') return payload
  if (canEditInventoryField(fieldName, permissions)) payload[fieldName] = values[fieldName]

  return payload
}, {})

const blankOptions = [{ id: '', name: 'Belum Diisi' }]
const sizeOptions = [
  { id: 'mini', name: 'Mini' },
  { id: 'small', name: 'Small' },
  { id: 'medium', name: 'Medium' },
  { id: 'large', name: 'Large' }
]
const internalCategoryOptions = [
  { id: '', name: '— Ditentukan Otomatis —' },
  { id: 'H', name: 'H — Hermes' },
  { id: 'W', name: 'W — Watch' },
  { id: 'C', name: 'C — Curated' }
]
const appraisalStateLabels = {
  need_appraisal      : 'Need Appraisal',
  need_re_appraisal   : 'Need Re-Appraisal',
  need_confirm_hm     : 'Confirm Buying Price',
  need_approval       : 'Need Approval',
  appraisal_approved  : 'Appraisal Approved',
  appraisal_rejected  : 'Appraisal Rejected'
}
const appraisalStateLabel = (product = {}) => product.appraisal_state_label || appraisalStateLabels[product.appraisal_state] || product.appraisal_state || '-'
const authenticationProviderOptions = [
  { id: '', name: 'Belum Diisi' },
  { id: 'bababebi', name: 'Bababebi' },
  { id: 'entrupy', name: 'Entrupy' },
  { id: 'legit_grails', name: 'Legit Grails' },
  { id: 'authentication_first', name: 'Authentication First' },
  { id: 'check_by_koko_watch', name: 'Check by Koko Watch' },
  { id: 'real_authentication', name: 'Real Authentication' }
]
const authenticationStatusOptions = [
  { id: 'not_checked', name: 'Not Checked' },
  { id: 'authentic', name: 'Authentic' },
  { id: 'not_authentic', name: 'Not Authentic' },
  { id: 'inconclusive', name: 'Inconclusive' }
]

const normalizeValue = (value) => {
  if (value === undefined || value === null) return ''

  return value
}

const normalizeIdValue = (value) => {
  if (value === undefined || value === null) return ''

  return String(value)
}

const normalizeArrayValue = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value.map(String)

  return [ String(value) ]
}

const normalizePhotoArray = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value

  if (typeof value === 'string') {
    return value
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return [ value ]
}

const normalizePhotoPreviewItem = (photo, index, fallbackLabel = 'Photo') => {
  if (!photo) return null

  if (typeof photo === 'string') {
    return {
      key   : `${fallbackLabel.toLowerCase().replace(/\s/g, '-')}-${index}`,
      label : `${fallbackLabel} ${index + 1}`,
      url   : photo
    }
  }

  const url = photo.url || photo.image_url || photo.photo_url || photo.src || photo.link
  if (!url) return null

  return {
    key            : photo.key || photo.id?.toString() || `${fallbackLabel.toLowerCase().replace(/\s/g, '-')}-${index}`,
    label          : photo.label || photo.name || photo.title || `${fallbackLabel} ${index + 1}`,
    url,
    attachmentId   : photo.attachment_id || photo.attachmentId,
    attachmentName : photo.attachment_name || photo.attachmentName
  }
}

const normalizePhotoPreviews = (value, fallbackLabel = 'Photo') => normalizePhotoArray(value)
  .map((photo, index) => normalizePhotoPreviewItem(photo, index, fallbackLabel))
  .filter(Boolean)

const uniquePhotoPreviews = (photos = []) => {
  const usedUrls = new Set()

  return photos.filter((photo) => {
    if (!photo?.url || usedUrls.has(photo.url)) return false

    usedUrls.add(photo.url)
    return true
  })
}

const inventoryProductUploadFiles = (values = {}) => inventoryProductUploadFileFields.flatMap((field) => {
  const value = values[field]
  if (!value) return []
  if (Array.isArray(value)) return value

  return [ value ]
}).filter((file) => file && typeof file.size === 'number')

const inventoryProductUploadValidationMessage = (uploadFiles = []) => {
  if (uploadFiles.length > INVENTORY_PRODUCT_UPLOAD_MAX_FILES) {
    return `Maksimal upload ${INVENTORY_PRODUCT_UPLOAD_MAX_FILES} file dalam 1 kali save.`
  }

  return ''
}

const inventoryProductFileSizeLabel = (file = {}) => {
  const size = Number(file.size || 0)
  if (!size) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`

  return `${(size / 1024 / 1024).toFixed(1)} MB`
}

const defaultBulkPhotoKind = (index) => inventoryProductBulkPhotoAutoKinds[index] || 'photo_details'

const buildBulkPhotoUploadRow = (file, index) => ({
  id         : `${Date.now()}-${index}-${file.name}`,
  file,
  kind       : defaultBulkPhotoKind(index),
  position   : index + 1,
  previewUrl : typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(file) : ''
})

const buildProductPhotoPreviews = (product = {}) => uniquePhotoPreviews([
  ...normalizePhotoPreviews(product.photos, 'Product Photo'),
  ...normalizePhotoPreviews(product.product_photos, 'Product Photo'),
  ...normalizePhotoPreviews(product.product_photo_urls, 'Product Photo'),
  ...normalizePhotoPreviews(product.product_photo_links, 'Product Photo'),
  ...normalizePhotoPreviews(product.product_image_links, 'Product Photo'),
  ...normalizePhotoPreviews(product.photo_links, 'Product Photo'),
  ...normalizePhotoPreviews(product.photo_details_urls, 'Photo Detail'),
  ...normalizePhotoPreviews(product.photo_details, 'Photo Detail'),
  ...normalizePhotoPreviews(product.completeness_photos, 'Completeness Photo'),
  normalizePhotoPreviewItem(product.main_image, 0, 'Main Photo'),
  normalizePhotoPreviewItem(product.main_image_url, 0, 'Main Photo'),
  normalizePhotoPreviewItem(product.product_photo_url, 0, 'Main Photo'),
  normalizePhotoPreviewItem(product.front_view_url || product.front_view, 0, 'Front View'),
  normalizePhotoPreviewItem(product.right_side_view_url || product.right_side_view, 0, 'Right Side View'),
  normalizePhotoPreviewItem(product.rear_view_url || product.rear_view, 0, 'Rear View'),
  normalizePhotoPreviewItem(product.left_side_view_url || product.left_side_view, 0, 'Left Side View'),
  normalizePhotoPreviewItem(product.top_view_url || product.top_view, 0, 'Top View'),
  normalizePhotoPreviewItem(product.bottom_view_url || product.bottom_view, 0, 'Bottom View'),
  normalizePhotoPreviewItem(product.comes_with_image_url || product.comes_with_image, 0, 'Foto Kelengkapan'),
  normalizePhotoPreviewItem(product.stamp_image_url || product.stamp_image, 0, 'Foto Stamp'),
  normalizePhotoPreviewItem(product.card_image_url || product.card_image, 0, 'Foto Card')
].filter(Boolean))

const optionsWithBlank = (options = []) => ([
  ...blankOptions,
  ...(options || [])
])

const OptionList = ({ options = [] }) => (
  <>
    {
      options.map((option) => (
        <option key={ `option-${option.id}` } value={ normalizeIdValue(option.id) }>
          { option.name || option.label || option.code || option.id }
        </option>
      ))
    }
  </>
)

OptionList.propTypes = optionPropTypes

const searchableOptionLabel = (option = {}) => {
  if (option.name && option.code) return `${option.name} (${option.code})`

  return option.name || option.label || option.code || option.id
}

const searchableSelectOptions = (options = []) => (options || [])
  .filter((option) => normalizeIdValue(option.id || option.value) !== '')
  .map((option) => ({
    value : normalizeIdValue(option.id || option.value),
    label : searchableOptionLabel(option),
    code  : option.code
  }))

const selectedSearchableOption = (options = [], value = '') => (
  searchableSelectOptions(options).find((option) => normalizeIdValue(option.value) === normalizeIdValue(value)) || null
)

const SearchableSelectField = ({ name, label, options = [], placeholder, disabled = false }) => {
  const [ field ] = useField(name)
  const { setFieldValue } = useFormikContext()
  const selectRef = useRef(null)
  const [ searchValue, setSearchValue ] = useState('')

  const handleInputChange = (nextValue, actionMeta = {}) => {
    if (actionMeta.action === 'input-change') {
      setSearchValue(nextValue)
    } else if ([ 'menu-close', 'set-value' ].includes(actionMeta.action)) {
      setSearchValue('')
    }

    return nextValue
  }

  const handleMenuOpen = () => {
    if (typeof window === 'undefined') return

    window.requestAnimationFrame(() => selectRef.current?.focus())
  }

  return (
    <FormGroup>
      <Label className='text-gray-light text-capitalize'>
        <strong>{ label }</strong>
      </Label>
      <Select
        ref={ selectRef }
        className='react-select success'
        classNamePrefix='react-select'
        isClearable
        isSearchable
        isDisabled={ disabled }
        openMenuOnFocus
        inputValue={ searchValue }
        options={ searchableSelectOptions(options) }
        value={ selectedSearchableOption(options, field.value) }
        onInputChange={ handleInputChange }
        onMenuOpen={ handleMenuOpen }
        onChange={ (option) => {
          setSearchValue('')
          setFieldValue(name, option?.value || '')
        } }
        placeholder={ placeholder || `Search ${label}` }
        noOptionsMessage={ ({ inputValue }) => inputValue ? 'No options found.' : 'Type to search.' }
      />
    </FormGroup>
  )
}

SearchableSelectField.propTypes = searchableSelectFieldPropTypes

const SectionTitle = ({ title, subtitle }) => (
  <div className='mb-3'>
    <h4 className='mb-1'>{ title }</h4>
    {
      subtitle ? (
        <p className='text-muted mb-0'>{ subtitle }</p>
      ) : null
    }
  </div>
)

SectionTitle.propTypes = {
  title    : PropTypes.string.isRequired,
  subtitle : PropTypes.string
}

const FileField = ({ name, label, multiple = false, disabled = false }) => {
  const { setFieldValue, values } = useFormikContext()
  const inputRef = useRef(null)
  const [ uploadError, setUploadError ] = useState('')

  const validateFiles = (nextValue) => inventoryProductUploadValidationMessage(
    inventoryProductUploadFiles({ ...values, [name]: nextValue })
  )

  const applyFiles = (files) => {
    const value = multiple ? Array.from(files || []) : files?.[0]
    const errorMessage = validateFiles(value || (multiple ? [] : null))
    if (errorMessage) {
      setUploadError(errorMessage)
      return false
    }

    setUploadError('')
    setFieldValue(name, value || (multiple ? [] : null))
    return true
  }

  const selectedValue = values[name]
  const selectedLabel = multiple
    ? (selectedValue?.length > 0 ? `${selectedValue.length} file(s) selected` : 'No file chosen')
    : (selectedValue?.name || 'No file chosen')

  return (
    <FormGroup>
      <Label className='text-gray-light text-capitalize'>
        <strong>{ label }</strong>
      </Label>
      <input
        ref={ inputRef }
        type='file'
        multiple={ multiple }
        disabled={ disabled }
        onChange={ (event) => {
          const accepted = applyFiles(event.currentTarget.files)
          if (!accepted) {
            event.currentTarget.value = ''
          }
        } }
        style={ { display: 'none' } }
      />
      <div
        role='button'
        tabIndex={ 0 }
        className='text-center'
        aria-disabled={ disabled }
        onClick={ () => {
          if (!disabled) inputRef.current?.click()
        } }
        onKeyDown={ (event) => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            inputRef.current?.click()
          }
        } }
        onDragOver={ (event) => {
          if (!disabled) event.preventDefault()
        } }
        onDrop={ (event) => {
          if (disabled) return

          event.preventDefault()
          const accepted = applyFiles(event.dataTransfer.files)
          if (!accepted && inputRef.current) inputRef.current.value = ''
        } }
        style={ {
          border       : '1px dashed #b8c2cc',
          borderRadius : 6,
          cursor       : disabled ? 'not-allowed' : 'pointer',
          padding      : '14px 12px',
          background   : disabled ? '#e9ecef' : '#f8fafc',
          opacity      : disabled ? 0.7 : 1
        } }
      >
        <span className='d-block font-weight-bold'>Drop file here</span>
        <span className='d-block text-muted small'>or click to choose</span>
        <span className='d-block text-muted small text-truncate mt-2'>{ selectedLabel }</span>
      </div>
      {
        uploadError ? (
          <small className='text-danger d-block mt-1'>{ uploadError }</small>
        ) : null
      }
    </FormGroup>
  )
}

FileField.propTypes = fileFieldPropTypes

const BulkPhotoUploadField = ({ pageUtils, disabled = false }) => {
  const { setFieldValue, values } = useFormikContext()

  const bulkPhotoFields = resolveInventoryBulkPhotoFields(
    values,
    photoFields
  )

  const bulkPhotoKindOptions = resolveInventoryBulkPhotoKindOptions(
    values,
    INVENTORY_PRODUCT_BULK_PHOTO_KIND_OPTIONS
  )

  const bulkSinglePhotoFields = bulkPhotoFields
    .filter((field) => !field.multiple)
    .map((field) => field.name)

  const [ rows, setRows ] = useState([])
  const [ isDragging, setIsDragging ] = useState(false)
  const [ bulkError, setBulkError ] = useState('')

  const syncRowsToForm = (nextRows) => {
    const sortedRows = [ ...nextRows ].sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    const groupedFiles = {}

    sortedRows.forEach((row) => {
      if (!row.kind || !row.file) return
      groupedFiles[row.kind] = groupedFiles[row.kind] || []
      groupedFiles[row.kind].push(row.file)
    })

    bulkPhotoFields.forEach((field) => {
      const files = groupedFiles[field.name] || []
      setFieldValue(field.name, field.multiple ? files : (files[files.length - 1] || null), false)
    })
  }

  const applyRows = (nextRows) => {
    setRows(nextRows)
    syncRowsToForm(nextRows)
  }

  const selectedBulkFiles = () => rows.map((row) => row.file).filter(Boolean)

  const manualUploadFiles = () => {
    const bulkFiles = selectedBulkFiles()

    return inventoryProductUploadFiles(values).filter((file) => !bulkFiles.includes(file))
  }

  const validateRows = (nextRows) => inventoryProductUploadValidationMessage([
    ...manualUploadFiles(),
    ...nextRows.map((row) => row.file).filter(Boolean)
  ])

  const handleFiles = (fileList) => {
    if (disabled) return

    const incomingFiles = Array.from(fileList || []).filter(Boolean)
    if (!incomingFiles.length) return

    const invalidFile = incomingFiles.find((file) => file.type && !file.type.startsWith('image/'))
    if (invalidFile) {
      setBulkError(`${invalidFile.name || 'File'} bukan image file. Upload hanya menerima image.`)
      return
    }

    const nextRows = [
      ...rows,
      ...incomingFiles.map((file, index) => buildBulkPhotoUploadRow(file, rows.length + index))
    ]
    const errorMessage = validateRows(nextRows)

    if (errorMessage) {
      nextRows.slice(rows.length).forEach((row) => {
        if (row.previewUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) URL.revokeObjectURL(row.previewUrl)
      })
      setBulkError(errorMessage)
      return
    }

    setBulkError('')
    applyRows(nextRows)
    pageUtils?.setAlertMsg?.(`${incomingFiles.length} photo ditambahkan. Pilih image type di table sebelum Save Product.`)
  }

  const updateRow = (rowId, attrs) => {
    const nextRows = rows.map((row) => row.id === rowId ? { ...row, ...attrs } : row)
    applyRows(nextRows)
  }

  const removeRow = (rowId) => {
    const removedRow = rows.find((row) => row.id === rowId)
    if (removedRow?.previewUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) URL.revokeObjectURL(removedRow.previewUrl)

    const nextRows = rows.filter((row) => row.id !== rowId)
    setBulkError('')
    applyRows(nextRows)
  }

  const clearRows = () => {
    rows.forEach((row) => {
      if (row.previewUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) URL.revokeObjectURL(row.previewUrl)
    })

    setBulkError('')
    applyRows([])
  }

  const duplicateSingleKinds = bulkSinglePhotoFields.filter((fieldName) => (
    rows.filter((row) => row.kind === fieldName).length > 1
  ))

  return (
    <div className='mb-4'>
      <div
        className={ `border rounded bg-light p-4 text-center ${isDragging ? 'border-primary' : ''}` }
        onDragOver={ (event) => {
          if (disabled) return

          event.preventDefault()
          setIsDragging(true)
        } }
        onDragLeave={ () => setIsDragging(false) }
        onDrop={ (event) => {
          if (disabled) return

          event.preventDefault()
          setIsDragging(false)
          handleFiles(event.dataTransfer.files)
        } }
      >
        <div className='font-weight-bold mb-2'>Drop multiple photos here</div>
        <div className='text-muted mb-3'>Atau klik tombol di bawah. Setelah masuk table, pilih kind/type image masing-masing.</div>
        <Input
          type='file'
          accept='image/*'
          multiple
          disabled={ disabled }
          onChange={ (event) => {
            handleFiles(event.currentTarget.files)
            event.currentTarget.value = ''
          } }
          style={ { maxWidth: 360, margin: '0 auto' } }
        />
      </div>

      {
        bulkError ? (
          <Alert color='danger' className='mt-3 mb-0'>
            { bulkError }
          </Alert>
        ) : null
      }

      {
        duplicateSingleKinds.length > 0 ? (
          <Alert color='warning' className='mt-3 mb-0'>
            { 'Ada lebih dari 1 file untuk single-photo slot. Sistem akan memakai file dengan position paling besar untuk slot tersebut: ' }
            { duplicateSingleKinds.join(', ') }
          </Alert>
        ) : null
      }

      {
        rows.length > 0 ? (
          <div className='table-responsive mt-3'>
            <table className='table table-bordered table-sm bg-white mb-2'>
              <thead>
                <tr>
                  <th style={ { width: 120 } }>Preview</th>
                  <th>File</th>
                  <th style={ { width: 260 } }>Image Type / Kind</th>
                  <th style={ { width: 120 } }>Position</th>
                  <th style={ { width: 100 } }>Size</th>
                  <th style={ { width: 90 } }>Action</th>
                </tr>
              </thead>
              <tbody>
                {
                  rows.map((row) => (
                    <tr key={ row.id }>
                      <td>
                        {
                          row.previewUrl ? (
                            <img
                              src={ row.previewUrl }
                              alt={ row.file?.name || 'Photo preview' }
                              style={ { width: 86, height: 86, objectFit: 'cover', borderRadius: 6 } }
                            />
                          ) : '-'
                        }
                      </td>
                      <td>
                        <div className='font-weight-bold'>{ row.file?.name || '-' }</div>
                        <small className='text-muted'>Will be uploaded after Save Product.</small>
                      </td>
                      <td>
                        <Input
                          type='select'
                          value={ row.kind }
                          disabled={ disabled }
                          onChange={ (event) => updateRow(row.id, { kind: event.currentTarget.value }) }
                        >
                          {
                            bulkPhotoKindOptions.map((option) => (
                              <option key={ option.value || 'blank' } value={ option.value }>
                                { option.label }
                              </option>
                            ))
                          }
                        </Input>
                      </td>
                      <td>
                        <Input
                          type='number'
                          min='1'
                          value={ row.position }
                          disabled={ disabled }
                          onChange={ (event) => updateRow(row.id, { position: event.currentTarget.value }) }
                        />
                      </td>
                      <td>{ inventoryProductFileSizeLabel(row.file) }</td>
                      <td>
                        <Button type='button' color='danger' size='sm' outline disabled={ disabled } onClick={ () => removeRow(row.id) }>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            <div className='d-flex justify-content-between align-items-center'>
              <small className='text-muted'>Rows with blank image type will be ignored. Photo details can contain multiple files—choose Photo details for every extra detail photo.</small>
              <Button type='button' color='secondary' size='sm' outline disabled={ disabled } onClick={ clearRows }>
                Clear Bulk Photos
              </Button>
            </div>
          </div>
        ) : null
      }
    </div>
  )
}

BulkPhotoUploadField.propTypes = bulkPhotoUploadFieldPropTypes

const SelectArrayDropdownField = ({ name, label, options = [], disabled = false }) => {
  const [ field ] = useField(name)
  const { setFieldValue } = useFormikContext()
  const selectedValue = Array.isArray(field.value) ? normalizeIdValue(field.value[0]) : normalizeIdValue(field.value)

  return (
    <FormGroup>
      <Label className='text-gray-light text-capitalize'>
        <strong>{ label }</strong>
      </Label>
      <Input
        type='select'
        name={ name }
        value={ selectedValue }
        disabled={ disabled }
        onChange={ (event) => {
          const value = event.currentTarget.value
          setFieldValue(name, value ? [ value ] : [])
        } }
        onBlur={ field.onBlur }
      >
        <OptionList options={ optionsWithBlank(options) } />
      </Input>
    </FormGroup>
  )
}

SelectArrayDropdownField.propTypes = selectArrayDropdownFieldPropTypes

const PhotoPreviewGrid = ({ photos = [], canDelete = false, deletingAttachmentId = null, onDeletePhoto = () => {} }) => {
  if (!photos.length) {
    return (
      <Alert color='light' className='border mb-4'>
        { 'Belum ada photo preview untuk produk ini. Upload foto melalui form di bawah lalu Save Product.' }
      </Alert>
    )
  }

  return (
    <div className='bg-white border rounded p-3 mb-4'>
      <Row form>
        {
          photos.map((photo, index) => (
            <Col xs={ 12 } sm={ 6 } md={ 4 } lg={ 3 } key={ photo.key || `photo-preview-${index}` } className='mb-3'>
              <div className='d-flex flex-column border rounded bg-white h-100'>
                <a
                  href={ photo.url }
                  target='_blank'
                  rel='noopener noreferrer'
                  className='d-block text-decoration-none'
                >
                  <div
                    className='d-flex align-items-center justify-content-center bg-light rounded-top'
                    style={ { height: 220, overflow: 'hidden', padding: 12 } }
                  >
                    <img
                      src={ photo.url }
                      alt={ photo.label || `Photo ${index + 1}` }
                      style={ { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' } }
                    />
                  </div>
                </a>
                <div className='d-flex align-items-center justify-content-between p-2 mt-auto'>
                  <div className='small font-weight-bold text-gray-light text-truncate mr-2'>
                    { photo.label || `Photo ${index + 1}` }
                  </div>
                  { canDelete && photo.attachmentId && (
                    <Button
                      type='button'
                      color='danger'
                      size='sm'
                      outline
                      disabled={ deletingAttachmentId?.toString() === photo.attachmentId.toString() }
                      onClick={ () => onDeletePhoto(photo) }
                    >
                      { deletingAttachmentId?.toString() === photo.attachmentId.toString() ? 'Deleting...' : 'Delete' }
                    </Button>
                  ) }
                </div>
              </div>
            </Col>
          ))
        }
      </Row>
    </div>
  )
}

PhotoPreviewGrid.propTypes = photoPreviewGridPropTypes

const InlineLegacyTabHeader = ({ activeTab, onTabChange }) => {
  const renderButton = (tab, label) => {
    const isActive = activeTab === tab

    return (
      <button
        type='button'
        onClick={ () => onTabChange(tab) }
        style={ {
          border          : 0,
          borderRight     : '1px solid #e8e8e8',
          backgroundColor : isActive ? '#ffffff' : '#f3f3f3',
          color           : '#333333',
          minWidth        : 96,
          padding         : '14px 22px',
          fontWeight      : isActive ? 600 : 400,
          cursor          : 'pointer',
          outline         : 'none'
        } }
      >
        { label }
      </button>
    )
  }

  return (
    <div
      className='d-flex align-items-end mb-4'
      style={ {
        backgroundColor : '#f3f3f3',
        borderBottom    : '1px solid #e8e8e8',
        margin          : '-20px -20px 24px',
        overflowX       : 'auto'
      } }
    >
      { renderButton(TAB.PRODUCT, 'Product') }
      { renderButton(TAB.PHOTOS, 'Photos') }
    </div>
  )
}

InlineLegacyTabHeader.propTypes = inlineTabHeaderPropTypes

const CheckboxField = ({ name, label, disabled = false }) => {
  const [ field ] = useField({ name, type: 'checkbox' })

  return (
    <FormGroup check className='mb-3'>
      <Label check>
        <Input
          type='checkbox'
          name={ name }
          checked={ !!field.value }
          disabled={ disabled }
          onChange={ field.onChange }
          onBlur={ field.onBlur }
        />
        { ' ' }{ label }
      </Label>
    </FormGroup>
  )
}

CheckboxField.propTypes = checkboxFieldPropTypes

const buildInitialValues = (inventory = {}) => {
  const product = inventory.product || {}

  return {
    sku                                : normalizeValue(product.sku || inventory.inventory_sku),
    group_id                           : normalizeIdValue(product.group_id),
    category_id                        : normalizeIdValue(product.category_id),
    designer_id                        : normalizeIdValue(product.designer_id),
    sellable_quantity                  : normalizeValue(inventory.sellable_quantity),
    inventory_quantity                 : normalizeValue(inventory.stored_quantity ?? inventory.inventory_quantity ?? inventory.quantity ?? product.inventory_quantity ?? product.quantity),
    inventory_status                   : product.status || '',
    show_unavailable_product           : inventory.show_unavailable_product ? 'Yes' : 'No',
    shipment_cost                      : normalizeValue(product.shipment_cost),
    authentication_cost                : normalizeValue(product.authentication_cost),
    total_modal_cost                   : normalizeValue(product.total_modal_cost),
    internal_category                  : normalizeValue(product.internal_category),
    name                               : normalizeValue(product.name),
    shipping_date                      : normalizeValue(product.shipping_date),
    inbound_at                         : normalizeValue(product.inbound_at),
    buying_price                       : normalizeValue(product.buying_price),
    default_price                      : normalizeValue(product.default_price),
    selling_price                      : normalizeValue(product.selling_price),
    appraisal_state_label              : normalizeValue(appraisalStateLabel(product)),
    appraisal_note                     : normalizeValue(product.appraisal_note),
    show_in_auction_page               : !!product.show_in_auction_page,
    price_tag_done                     : !!product.price_tag_done,
    supplier_name                      : normalizeValue(product.supplier_name),
    comes_with                         : normalizeValue(product.comes_with),
    product_description                : normalizeValue(product.product_description),
    stamp                              : normalizeValue(product.stamp),
    leather                            : normalizeValue(product.leather),
    hardware                           : normalizeValue(product.hardware),
    p_colour                           : normalizeValue(product.p_colour),
    details                            : normalizeValue(product.details_text),
    p_size                             : normalizeValue(product.p_size),
    insole_size                        : normalizeValue(product.insole_size),
    height                             : normalizeValue(product.height),
    length                             : normalizeValue(product.length),
    width                              : normalizeValue(product.width),
    handle_drop                        : normalizeValue(product.handle_drop),
    strap_drop                         : normalizeValue(product.strap_drop),
    condition_id                       : normalizeIdValue(product.condition_id),
    defects                            : normalizeValue(product.defects),
    additional_condition_description   : normalizeValue(product.additional_condition_description),
    size                               : normalizeValue(product.size),
    colour_ids                         : normalizeArrayValue(product.colour_ids),
    badge_ids                          : normalizeArrayValue(product.badge_ids),
    style_ids                          : normalizeArrayValue(product.style_ids),
    item_location_id                   : normalizeIdValue(product.item_location_id),
    box_location_id                    : normalizeIdValue(product.box_location_id),
    inclusions_location_id             : normalizeIdValue(product.inclusions_location_id),
    internal_search_keyword            : normalizeValue(product.internal_search_keyword),
    internal_note                      : normalizeValue(product.internal_note),
    product_curator_pic                : normalizeValue(product.product_curator_pic),
    sourced_by                         : normalizeValue(product.sourced_by),
    receiver_pic                       : normalizeValue(product.receiver_pic),
    stockist_pic                       : normalizeValue(product.stockist_pic),
    photo_folder_link                  : normalizeValue(product.photo_folder_link),
    video_links                        : normalizeValue(product.video_links),
    authentication_provider            : normalizeValue(product.authentication_provider),
    authentication_provider_other      : normalizeValue(product.authentication_provider_other),
    authentication_status              : normalizeValue(product.authentication_status || 'not_checked'),
    authentication_certificate_link    : normalizeValue(product.authentication_certificate_link),
    authentication_notes               : normalizeValue(product.authentication_notes),
    authentication_certificate         : null,
    front_view                         : null,
    right_side_view                    : null,
    rear_view                          : null,
    left_side_view                     : null,
    top_view                           : null,
    bottom_view                        : null,
    photo_details                      : [],
    comes_with_image                   : null,
    stamp_image                        : null,
    card_image                         : null,
    insole_photo                     : null
  }
}

const InventoryEditPage = ({ pageUtils }) => {
  const [ inventory, setInventory ] = useState({})
  const [ options, setOptions ] = useState({})
  const [ isLoading, setIsLoading ] = useState(true)
  const [ isSaving, setIsSaving ] = useState(false)
  const [ activeTab, setActiveTab ] = useState(TAB.PRODUCT)
  const [ pendingLowPriceSubmit, setPendingLowPriceSubmit ] = useState(null)
  const [ isQuantityHintExpanded, setIsQuantityHintExpanded ] = useState(false)
  const [ deletingPhotoAttachmentId, setDeletingPhotoAttachmentId ] = useState(null)
  const [ deletedPhotoAttachmentIds, setDeletedPhotoAttachmentIds ] = useState([])
  const [ photoWarning, setPhotoWarning ] = useState({ isOpen: false, missing: [] })
  const pendingSaveRef = useRef(null)

  const { id } = useParams()
  const history = useHistory()
  const { user } = useAuth()

  const fetchPage = useCallback(() => {
    setIsLoading(true)

    Promise.all([
      InventoriesApiV2.show(id),
      InventoriesApiV2.editOptions()
    ])
      .then(([ inventoryResponse, optionsResponse ]) => {
        setInventory(inventoryResponse.data.inventory)
        setOptions(optionsResponse.data.options || {})
        setDeletedPhotoAttachmentIds([])
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          pageUtils.setAlertMsg('Anda tidak memiliki akses untuk Edit Inventory.', ALERT_TYPES.DANGER)
          history.push({ pathname: `/app/warehouse-team/inventory/${id}` })
        }
        else {
          pageUtils.setApiErrorMsg(error.response?.data || { message: 'Unable to fetch inventory edit data' })
        }
      })
      .finally(() => setIsLoading(false))
  }, [ history, id, pageUtils ])

  useEffect(() => {
    fetchPage()
  }, [ fetchPage ])

  const initialValues = useMemo(() => buildInitialValues(inventory), [ inventory ])
  const productPhotos = useMemo(() => buildProductPhotoPreviews(inventory.product).filter((photo) => (
    !photo.attachmentId || !deletedPhotoAttachmentIds.includes(photo.attachmentId.toString())
  )), [ deletedPhotoAttachmentIds, inventory ])
  const pricePermissions = useMemo(() => normalizeInventoryPricePermissions(inventory.permissions || {}), [ inventory.permissions ])
  const editPermissions = useMemo(() => normalizeInventoryEditPermissions(inventory.permissions || {}), [ inventory.permissions ])
  const canViewAppraisalNote = inventory.permissions?.can_view_appraisal_note === true
  const isWarehouseOrWeb = [ 'warehouse', 'web' ].includes(String(user?.role || '').trim().toLowerCase())
  const requiresShoesSizes = (values) => isWarehouseOrWeb && isInventoryShoesProduct(values)
  const visiblePriceFields = useMemo(() => (
    priceFields.filter((field) => !field.viewPermission || pricePermissions[field.viewPermission])
  ), [ pricePermissions ])

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const handleBackToDetail = () => {
    history.push({ pathname: `/app/warehouse-team/inventory/${id}` })
  }

  const handleDeletePhoto = async (photo = {}) => {
    if (!photo.attachmentId || deletingPhotoAttachmentId) return

    const confirmed = window.confirm(`Delete ${photo.label || 'this image'}? This action cannot be undone.`)
    if (!confirmed) return

    setDeletingPhotoAttachmentId(photo.attachmentId)
    try {
      const response = await InventoriesApiV2.deletePhoto(id, photo.attachmentId)
      setDeletedPhotoAttachmentIds((currentIds) => [ ...currentIds, photo.attachmentId.toString() ])
      pageUtils.setAlertMsg(response.data?.message || 'Photo deleted successfully.', ALERT_TYPES.SUCCESS)
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { message: 'Unable to delete inventory product photo' })
    } finally {
      setDeletingPhotoAttachmentId(null)
    }
  }

  const hasInvalidRequiredShoesSizes = (values, formikBag) => {
    if (!requiresShoesSizes(values)) return false

    const error = validateInventoryShoesRequiredFields(values, values)
    if (!error) return false

    const fieldName = error.startsWith('Product Size') ? 'p_size' : 'insole_size'
    formikBag.setFieldError(fieldName, error)
    formikBag.setFieldTouched(fieldName, true, false)
    formikBag.setSubmitting(false)
    setActiveTab(TAB.PRODUCT)
    pageUtils.setAlertMsg(error, ALERT_TYPES.DANGER)
    return true
  }

  const saveProduct = async (values, formikBag, confirmLowSellingPrice = false) => {
    if (hasInvalidRequiredShoesSizes(values, formikBag)) return

    setIsSaving(true)
    formikBag.setSubmitting(true)

    const payloadValues = prepareInventoryShoesUpdatePayload(
      values,
      values,
      editableInventoryPayload(values, editPermissions)
    )

    if (!pricePermissions.canViewBuyingPrice) delete payloadValues.buying_price
    if (!pricePermissions.canViewDefaultPrice) delete payloadValues.default_price
    if (!pricePermissions.canViewSellingPrice) delete payloadValues.selling_price

    await InventoriesApiV2.updateProduct(id, payloadValues, { confirmLowSellingPrice })
      .then((response) => {
        setInventory(response.data.inventory)
        pageUtils.setAlertMsg(response.data.message || 'Inventory product data has been updated.', ALERT_TYPES.SUCCESS)
        history.push({ pathname: `/app/warehouse-team/inventory/${id}` })
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response?.data || { message: 'Unable to update inventory product data' })
        const messages = error.response?.data?.messages || {}
        formikBag.setErrors(Object.fromEntries(Object.entries(messages).map(([ key, value ]) => [ key, Array.isArray(value) ? value.join(' ') : value ])))
        formikBag.setTouched({ ...formikBag.touched, ...Object.fromEntries(Object.keys(messages).map((key) => [ key, true ])) }, false)
        window.alert(messages.base || error.response?.data?.message || 'Gagal menyimpan produk. Periksa field yang berwarna merah.')
      })
      .finally(() => {
        setIsSaving(false)
        formikBag.setSubmitting(false)
      })
  }

  const missingRequiredHPhotos = (values = {}) => {
    if (String(values.internal_category || '').toUpperCase() !== 'H') return []

    const product = inventory.product || {}
    const requirements = [
      { field: 'stamp_image', existing: product.stamp_image_url || product.stamp_image, label: 'Foto Stamp' },
      { field: 'card_image', existing: product.card_image_url || product.card_image, label: 'Foto Card' }
    ]

    return requirements
      .filter(({ field, existing }) => !existing && !values[field])
      .map(({ label }) => label)
  }

  const missingRequiredShoesWarnings = (values = {}) => {
    const product = inventory.product || {}
    const hasInsolePhoto = Boolean(
      values.insole_photo ||
      product.insole_photo_url ||
      product.insole_photo
    )

    return getInventoryShoesMissingRequiredWarnings(
      product,
      values,
      { hasInsolePhoto }
    ).filter((label) => label === 'Insole View')
  }

  const missingRequiredWarnings = (values = {}) => [
    ...missingRequiredHPhotos(values),
    ...missingRequiredShoesWarnings(values)
  ]

  const continueSubmitAfterPhotoCheck = async (values, formikBag) => {
    const sellingPrice = Number(values.selling_price)
    const buyingPrice = Number(values.buying_price)
    const isBelowBuyingPrice =
      Number.isFinite(sellingPrice) &&
      Number.isFinite(buyingPrice) &&
      sellingPrice < buyingPrice

    if (!isBelowBuyingPrice) {
      await saveProduct(values, formikBag)
      return
    }

    if (!pricePermissions.canSetSellingPriceBelowBuyingPrice) {
      pageUtils.setAlertMsg('Akun ini tidak diizinkan menyimpan selling price di bawah buying price.', ALERT_TYPES.DANGER)
      formikBag.setSubmitting(false)
      return
    }

    setPendingLowPriceSubmit({ values, formikBag })
  }

  const handleSubmit = async (values, formikBag) => {
    if (editPermissions.isReadOnly) return
    if (hasInvalidRequiredShoesSizes(values, formikBag)) return

    const missingPhotos = missingRequiredWarnings(values)

    if (missingPhotos.length > 0) {
      pendingSaveRef.current = { values, formikBag }
      setPhotoWarning({ isOpen: true, missing: missingPhotos })
      formikBag.setSubmitting(false)
      return
    }

    await continueSubmitAfterPhotoCheck(values, formikBag)
  }

  const confirmLowSellingPrice = async () => {
    if (!pendingLowPriceSubmit) return

    const pendingSubmit = pendingLowPriceSubmit
    setPendingLowPriceSubmit(null)
    await saveProduct(pendingSubmit.values, pendingSubmit.formikBag, true)
  }

  const closePhotoWarning = () => {
    pendingSaveRef.current?.formikBag?.setSubmitting(false)
    pendingSaveRef.current = null
    setPhotoWarning({ isOpen: false, missing: [] })
  }

  const completeMissingRequirements = () => {
    const targetTab = resolveInventoryShoesMissingRequirementTab(
      photoWarning.missing
    )

    closePhotoWarning()
    setActiveTab(targetTab)
  }

  const confirmSaveWithMissingPhotos = async () => {
    const pendingSave = pendingSaveRef.current
    pendingSaveRef.current = null
    setPhotoWarning({ isOpen: false, missing: [] })

    if (!pendingSave) return

    await continueSubmitAfterPhotoCheck(pendingSave.values, pendingSave.formikBag)
  }

  if (isLoading) {
    return (
      <Card body>
        <div className='d-flex align-items-center'>
          <Spinner size='sm' className='mr-2' />
          { 'Loading inventory edit data...' }
        </div>
      </Card>
    )
  }

  return (
    <>
      <Formik
        enableReinitialize={ true }
        initialValues={ initialValues }
        onSubmit={ handleSubmit }
      >
        { (formikBag) => (
          <Form>
            <div
              className='d-flex justify-content-between align-items-center flex-wrap mb-4'
              style={ { gap: 16 } }
            >
              <TitlePage mainTitle={ 'Edit Inventory' } subTitle={ inventory.inventory_sku || '-' } />

              <div
                className='d-flex align-items-center justify-content-end flex-wrap'
                style={ { gap: 12 } }
              >
                <Button color='secondary' outline type='button' onClick={ handleBackToDetail }>
                  { 'Back to Detail' }
                </Button>

                <Button color='success' type='submit' disabled={ formikBag.isSubmitting || isSaving || editPermissions.isReadOnly }>
                  {
                    formikBag.isSubmitting || isSaving ? (
                      <>
                        <Spinner size='sm' className='mr-2' />
                        { 'Saving...' }
                      </>
                    ) : editPermissions.isReadOnly ? 'Read Only' : 'Save Product'
                  }
                </Button>
              </div>
            </div>

            <Alert color={ editPermissions.isReadOnly ? 'secondary' : 'info' }>
              {
                editPermissions.isReadOnly
                  ? 'Akses hanya-baca: seluruh field tidak dapat diubah.'
                  : editPermissions.canEditAllProductFields
                    ? editPermissions.editablePriceFields.length === 0
                      ? 'Akses edit produk tersedia. Default Price dan Selling Price hanya-baca; Buying Price disembunyikan.'
                      : 'Akses edit produk tersedia sesuai izin field dan harga.'
                    : 'Field yang dapat diubah sudah dibatasi sesuai role. Field lainnya ditampilkan sebagai read-only.'
              }
            </Alert>

            <Card>
              <CardBody>
                <InlineLegacyTabHeader activeTab={ activeTab } onTabChange={ toggleTab } />

                <TabContent activeTab={ activeTab }>
                  <TabPane tabId={ TAB.PRODUCT }>
                    <SectionTitle
                      title='Product'
                      subtitle='Form ini mengikuti field edit produk production dashboard. Detail Inventory tetap view-only untuk semua user.'
                    />

                    <Row form>
                      <Col xs={ 12 } md={ 12 }>
                        <InputField name='sku' label='SKU' disabled={ !editPermissions.canEditSku } formText='SKU harus sesuai Group, Category, dan Designer. Internal Category disinkronkan otomatis setelah disimpan.' />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='select' name='group_id' label='Group' disabled={ !canEditInventoryField('group_id', editPermissions) }>
                          <OptionList options={ optionsWithBlank(options.groups) } />
                        </InputField>
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='select' name='category_id' label='Category' disabled={ !canEditInventoryField('category_id', editPermissions) }>
                          <OptionList options={ optionsWithBlank(options.categories) } />
                        </InputField>
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='select' name='designer_id' label='Designer' disabled={ !canEditInventoryField('designer_id', editPermissions) }>
                          <OptionList options={ optionsWithBlank(options.designers) } />
                        </InputField>
                      </Col>
                    </Row>

                    <Row form>
                      <Col xs={ 12 } md={ 6 }>
                        <InputField name='sellable_quantity' label='Sellable Quantity' disabled />
                      </Col>
                      <Col xs={ 12 } md={ 6 }>
                        <InputField name='inventory_quantity' label='Inventory Quantity (Stored)' disabled />
                      </Col>
                    </Row>

                    <div className='mb-3'>
                      <Button
                        type='button'
                        color='info'
                        outline
                        size='sm'
                        onClick={ () => setIsQuantityHintExpanded((expanded) => !expanded) }
                      >
                        { isQuantityHintExpanded ? 'Sembunyikan Panduan Quantity' : 'Tampilkan Panduan Quantity' }
                      </Button>
                    </div>

                    {
                      isQuantityHintExpanded ? (
                        <Row form>
                          <Col xs={ 12 } md={ 6 }>
                            <Alert color='info'>
                              <strong>{ 'Catatan:' }</strong>
                              { ' angka di atas adalah sellable quantity, yaitu quantity yang saat ini masih bisa dijual. Ini bukan total stok fisik, bukan source quantity inbound/consignment, dan bukan selalu berarti barang fisiknya tidak ada.' }
                              <div className='mt-2'>
                                { 'Status Inventory: ' }<strong>{ formikBag.values.inventory_status || '-' }</strong>
                                { ' | Tampilkan sebagai tidak tersedia di website/app: ' }<strong>{ formikBag.values.show_unavailable_product || '-' }</strong>
                              </div>
                            </Alert>
                          </Col>
                          <Col xs={ 12 } md={ 6 }>
                            <Alert color='info'>
                              <strong>{ 'Catatan:' }</strong>
                              { ' angka di atas adalah inventory quantity, yaitu jumlah barang fisik yang saat ini masih tersimpan di inventory.' }
                            </Alert>
                          </Col>
                        </Row>
                      ) : null
                    }

                    { pricePermissions.canViewCosts && <Row form>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='number' name='shipment_cost' label='Shipment Cost' disabled={ !canEditInventoryField('shipment_cost', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='number' name='authentication_cost' label='Authentication Cost' disabled={ !canEditInventoryField('authentication_cost', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='number' name='total_modal_cost' label='Total Modal (Auto)' disabled />
                      </Col>
                    </Row> }

                    <InputField type='select' name='internal_category' label='Internal Category (Otomatis)' disabled>
                      <OptionList options={ internalCategoryOptions } />
                    </InputField>

                    <InputField name='name' label='Name' disabled={ !canEditInventoryField('name', editPermissions) } />

                    <Row form>
                      <Col xs={ 12 } md={ 12 }>
                        <InputField type='datetime-local' name='shipping_date' label='Inbound Date' disabled={ !canEditInventoryField('shipping_date', editPermissions) } />
                      </Col>
                    </Row>

                    <Row form>
                      {
                        visiblePriceFields.map((field) => {
                          const isReadOnly = field.editPermission && !pricePermissions[field.editPermission]

                          return (
                            <Col xs={ 12 } md={ 4 } key={ field.name }>
                              <InputField
                                type='number'
                                name={ field.name }
                                label={ field.label }
                                disabled={ isReadOnly }
                                formText={ isReadOnly ? 'Read-only. Anda tidak memiliki akses untuk mengubah harga ini.' : undefined }
                              />
                            </Col>
                          )
                        })
                      }
                      <Col xs={ 12 } md={ 4 }>
                        <InputField name='appraisal_state_label' label='Appraisal State' disabled />
                      </Col>
                      {
                        canViewAppraisalNote ? (
                          <Col xs={ 12 } md={ 4 }>
                            <InputField name='appraisal_note' label='Appraisal Note' disabled={ !canEditInventoryField('appraisal_note', editPermissions) } />
                          </Col>
                        ) : null
                      }
                    </Row>

                    <Row form>
                      <Col xs={ 12 } md={ 6 }>
                        <CheckboxField name='show_in_auction_page' label='Show in auction page'  disabled={ !canEditInventoryField('show_in_auction_page', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 6 }>
                        <CheckboxField name='price_tag_done' label='Price tag done'  disabled={ !canEditInventoryField('price_tag_done', editPermissions) } />
                      </Col>
                    </Row>

                    <Row form>
                      {
                        textFieldsLeft.map((field) => (
                          <Col xs={ 12 } md={ 6 } key={ field.name }>
                            <InputField name={ field.name } label={ field.label } disabled={ !canEditInventoryField(field.name, editPermissions) } />
                          </Col>
                        ))
                      }
                    </Row>

                    <Row form>
                      <Col xs={ 12 } md={ 6 }>
                        <InputField type='textarea' name='comes_with' label='Comes with' disabled={ !canEditInventoryField('comes_with', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 6 }>
                        <InputField type='textarea' name='product_description' label='Product description' disabled={ !canEditInventoryField('product_description', editPermissions) } />
                      </Col>
                    </Row>

                    <InputField type='textarea' name='details' label='Details' disabled={ !canEditInventoryField('details', editPermissions) } />

                    <Row form>
                      {
                        measurements.map((field) => (
                          <Col xs={ 12 } md={ 4 } key={ field.name }>
                            <InputField
                              name={ field.name }
                              label={ field.name === 'p_size' && requiresShoesSizes(formikBag.values)
                                  ? <>{ field.label } <span className='text-danger'>*</span></>
                                  : field.label }
                              disabled={ !canEditInventoryField(field.name, editPermissions) }
                              formText={
                                field.name === 'p_size' && isInventoryShoesProduct(formikBag.values)
                                  ? 'Shoes: angka saja tanpa prefix EU / US / UK / AUS. Desimal boleh memakai titik atau koma.'
                                  : undefined
                              }
                            />
                          </Col>
                        ))
                      }
                    </Row>

                    {
                      isInventoryShoesProduct(formikBag.values) ? (
                        <Row form>
                          <Col xs={ 12 } md={ 4 }>
                            <InputField
                              name='insole_size'
                              label='Insole Size'
                              disabled={ !canEditInventoryField('insole_size', editPermissions) }
                              formText='Khusus Shoes. Masukkan angka lebih dari 0; desimal boleh memakai titik atau koma.'
                            />
                          </Col>
                        </Row>
                      ) : null
                    }

                    <InputField type='select' name='condition_id' label='Condition' disabled={ !canEditInventoryField('condition_id', editPermissions) }>
                      <OptionList options={ optionsWithBlank(options.conditions) } />
                    </InputField>
                    <InputField type='textarea' name='defects' label='Defects (Dosa)' disabled={ !canEditInventoryField('defects', editPermissions) } />
                    <InputField type='select' name='size' label='Size' disabled={ !canEditInventoryField('size', editPermissions) }>
                      <OptionList options={ optionsWithBlank(sizeOptions) } />
                    </InputField>

                    <Row form>
                      <Col xs={ 12 } md={ 4 }>
                        <SelectArrayDropdownField name='colour_ids' label='Colours' options={ options.colours || [] }  disabled={ !canEditInventoryField('colour_ids', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <SelectArrayDropdownField name='badge_ids' label='Badges' options={ options.badges || [] }  disabled={ !canEditInventoryField('badge_ids', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <SelectArrayDropdownField name='style_ids' label='Styles' options={ options.styles || [] }  disabled={ !canEditInventoryField('style_ids', editPermissions) } />
                      </Col>
                    </Row>

                    <Row form>
                      <Col xs={ 12 } md={ 4 }>
                        <SearchableSelectField name='item_location_id' label='Lokasi Produk' options={ options.item_locations || [] }  disabled={ !canEditInventoryField('item_location_id', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <SearchableSelectField name='box_location_id' label='Data Rak (Lokasi Box)' options={ options.box_locations || [] }  disabled={ !canEditInventoryField('box_location_id', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <SearchableSelectField name='inclusions_location_id' label='Lokasi Kelengkapan' options={ options.inclusions_locations || [] }  disabled={ !canEditInventoryField('inclusions_location_id', editPermissions) } />
                      </Col>
                    </Row>

                    <InputField name='internal_search_keyword' label='Internal search keyword' disabled={ !canEditInventoryField('internal_search_keyword', editPermissions) } />
                    <InputField type='textarea' name='internal_note' label='Catatan Internal' disabled={ !canEditInventoryField('internal_note', editPermissions) } />
                    { ENABLE_CONSIGNMENT_NOTES && (
                      <FormGroup>
                        <Label>Consignment Notes</Label>
                        <Input type='textarea' rows='3' value={ inventory.product?.consignment_notes || '' } placeholder='No consignment notes recorded.' readOnly />
                        { Object.prototype.hasOwnProperty.call(inventory.product || {}, 'consignment_notes')
                          ? <small className='text-muted'>Notes from Create Consignment (read-only).</small>
                          : <small className='text-warning'>Consignment Notes is not included in the API response. Check backend deployment.</small> }
                      </FormGroup>
                    ) }

                    <Row form>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField name='product_curator_pic' label='PIC Product Curator' disabled={ !canEditInventoryField('product_curator_pic', editPermissions) } />
                        { String(inventory.product?.product_curator_pic || '').trim() !== '' &&
                          <div className='form-text text-muted'>Konfirmasi ke Head Sales/ Commercial Jika ini sebuah kesalahan</div> }
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField name='receiver_pic' label='PIC Penerima' disabled={ !canEditInventoryField('receiver_pic', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField name='stockist_pic' label='PIC Stockist (Foto Detail)' disabled={ !canEditInventoryField('stockist_pic', editPermissions) } />
                      </Col>
                    </Row>
                    { String(inventory.product?.sku || '').toUpperCase().startsWith('BKO-') && <Row form>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='select' name='sourced_by' label='PIC Sourced By (Buying Team)' disabled={ !canEditInventoryField('sourced_by', editPermissions) }>
                          <option value=''>Pilih Buying Team</option>
                          { (options.sourcing_buyers || []).map((buyer) => <option key={ buyer.value } value={ buyer.value }>{ buyer.label }</option>) }
                        </InputField>
                      </Col>
                    </Row> }
                  </TabPane>

                  <TabPane tabId={ TAB.PHOTOS }>
                    <SectionTitle
                      title='Photos Preview'
                      subtitle='Preview foto existing seperti tab Photos di production dashboard. Klik foto untuk membuka ukuran penuh.'
                    />
                    <PhotoPreviewGrid
                      photos={ productPhotos }
                      canDelete={ editPermissions.canEditPhotos }
                      deletingAttachmentId={ deletingPhotoAttachmentId }
                      onDeletePhoto={ handleDeletePhoto }
                    />

                    <Alert color='info' className='mb-4'>
                      { 'Upload di bawah akan mengganti/menambahkan foto setelah tombol Save Product ditekan.' }
                    </Alert>

                    <SectionTitle
                      title='Bulk Upload Photos'
                      subtitle='Drop multiple photo files, lalu pilih kind/type image dalam table. Maksimal 40 file dalam 1 kali save. Validasi ukuran file dimatikan sementara sampai auto-compress aktif.'
                    />
                    <Alert color='secondary' className='mb-3'>
                      { 'Photo Details boleh di-upload lebih dari 1 file. Pilih image type “Photo details” untuk setiap foto detail tambahan.' }
                    </Alert>
                    <BulkPhotoUploadField pageUtils={ pageUtils } disabled={ !editPermissions.canEditPhotos } />

                    {
                      isInventoryShoesProduct(formikBag.values) && inventory.product?.insole_photo_url ? (
                        <Alert color='secondary'>
                          { 'Current Insole View: ' }
                          <a
                            href={ inventory.product.insole_photo_url }
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            Open Insole View
                          </a>
                        </Alert>
                      ) : null
                    }

                    <InputField name='photo_folder_link' label='Link Foto Produk (Google Drive)' disabled={ !canEditInventoryField('photo_folder_link', editPermissions) } />
                    <SectionTitle
                      title='Authentication'
                      subtitle='Pilih third party, upload certificate, dan isi hasil authentication product.'
                    />
                    <Row form>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='select' name='authentication_provider' label='Authentication Third Party' disabled={ !canEditInventoryField('authentication_provider', editPermissions) }>
                          <OptionList options={ authenticationProviderOptions } />
                        </InputField>
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField name='authentication_provider_other' label='Check by Koko Watch / Other Name' disabled={ !canEditInventoryField('authentication_provider_other', editPermissions) } />
                      </Col>
                      <Col xs={ 12 } md={ 4 }>
                        <InputField type='select' name='authentication_status' label='Authentication Result' disabled={ !canEditInventoryField('authentication_status', editPermissions) }>
                          <OptionList options={ authenticationStatusOptions } />
                        </InputField>
                      </Col>
                    </Row>
                    <InputField name='authentication_certificate_link' label='Authenticator Link / Certificate Link' disabled={ !canEditInventoryField('authentication_certificate_link', editPermissions) } />
                    {
                      inventory.product?.authentication_certificate_url ? (
                        <Alert color='secondary'>
                          { 'Current Authentication Certificate: ' }
                          <a href={ inventory.product.authentication_certificate_url } target='_blank' rel='noopener noreferrer'>
                            { 'Open certificate' }
                          </a>
                        </Alert>
                      ) : null
                    }
                    <FileField name='authentication_certificate' label='Upload Authentication Certificate'  disabled={ !canEditInventoryField('authentication_certificate', editPermissions) } />
                    <InputField type='textarea' name='authentication_notes' label='Authentication Notes' placeholder='Example: Authentic, not authentic, cert number, or extra notes.' disabled={ !canEditInventoryField('authentication_notes', editPermissions) } />
                    <InputField type='textarea' name='video_links' label='Video links' placeholder='Use comma separated video links.' disabled={ !canEditInventoryField('video_links', editPermissions) } />
                  </TabPane>
                </TabContent>
              </CardBody>

            </Card>
          </Form>
        ) }
      </Formik>

      <Modal isOpen={ !!pendingLowPriceSubmit } toggle={ () => setPendingLowPriceSubmit(null) }>
        <ModalHeader toggle={ () => setPendingLowPriceSubmit(null) }>
          Confirm Selling Price
        </ModalHeader>
        <ModalBody>
          Selling price lebih rendah daripada buying price. Pastikan harga sudah benar sebelum menyimpan perubahan ini.
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' outline onClick={ () => setPendingLowPriceSubmit(null) }>
            Cancel
          </Button>
          <Button color='danger' onClick={ confirmLowSellingPrice } disabled={ isSaving }>
            Confirm & Save
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={ photoWarning.isOpen } toggle={ closePhotoWarning }>
        <ModalHeader toggle={ closePhotoWarning }>
          {
            photoWarning.missing.some(
              (label) => [ 'Product Size', 'Insole Size', 'Insole View' ].includes(label)
            )
              ? 'Data Shoes Belum Lengkap'
              : 'Photo Stamp / Card Belum Lengkap'
          }
        </ModalHeader>
        <ModalBody>
          <p>
            {
              photoWarning.missing.some(
                (label) => [ 'Product Size', 'Insole Size', 'Insole View' ].includes(label)
              )
                ? 'Product Shoes belum memiliki data wajib berikut:'
                : 'Product category H belum memiliki photo wajib berikut:'
            }
          </p>
          <ul className='mb-3'>
            {
              photoWarning.missing.map((label) => (
                <li key={ label }><strong>{ label }</strong></li>
              ))
            }
          </ul>
          <p className='mb-0'>
            { 'Ini hanya last warning. Anda masih bisa tetap menyimpan product tanpa photo tersebut.' }
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' type='button' onClick={ completeMissingRequirements } disabled={ isSaving }>
            {
              photoWarning.missing.some(
                (label) => [ 'Product Size', 'Insole Size' ].includes(label)
              )
                ? 'Lengkapi Data'
                : 'Lengkapi Foto'
            }
          </Button>
          <Button color='warning' type='button' onClick={ confirmSaveWithMissingPhotos } disabled={ isSaving }>
            { isSaving ? 'Saving...' : 'Tetap Save' }
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

InventoryEditPage.propTypes = propTypes

export default InventoryEditPage
